import { Link } from "react-router-dom";
import { Heart, ShoppingBag, MapPin, Star, Truck, BadgeCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const WINE_LABEL = {
    tinto: "Tinto", branco: "Branco", rose: "Rosé", rosé: "Rosé",
    espumante: "Espumante", porto: "Porto", madeira: "Madeira", verde: "Verde",
};

export default function ProductCard({ product, onFavorite, isFavorite }) {
    const { add } = useCart();
    const w = product.wine || {};
    const isWine = product.kind === "wine";
    const stock = product.stock ?? 0;
    const out = stock <= 0;
    const low = !out && stock <= 3;

    function handleBuy(e) {
        e.preventDefault();
        e.stopPropagation();
        if (out) return;
        add(product, 1);
        toast.success(`${product.name} adicionado ao carrinho`);
    }

    // Linha de metadados: vinho mostra ficha técnica; produto mostra categoria
    const wineMeta = isWine
        ? [w.vintage_year, WINE_LABEL[(w.wine_type || "").toLowerCase()] || w.wine_type,
           w.alcohol_pct ? `${w.alcohol_pct}%` : null].filter(Boolean).join(" · ")
        : product.category;

    const rating = product.avg_rating || 0;
    const reviews = product.reviews_count || 0;
    const rounded = Math.round(rating);

    return (
        <Link
            to={`/produto/${product.id}`}
            className="product-card group flex flex-col bg-white border border-gold-soft/25 hover:border-gold-soft/70 hover:shadow-2xl rounded-lg overflow-hidden transition-all duration-500"
            data-testid={`product-card-${product.id}`}
        >
            <div className="relative aspect-square overflow-hidden bg-cream-warm">
                <img
                    src={product.images?.[0]}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                />
                <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
                    <div className="flex flex-col gap-1.5">
                        {product.limited_edition && (
                            <span className="bg-wine-dark/95 text-gold-soft label-overline text-[9px] px-2.5 py-1 rounded-sm shadow-sm">
                                Edição Limitada
                            </span>
                        )}
                        <span className="bg-cream/90 backdrop-blur-sm text-wine label-overline text-[9px] px-2.5 py-1 rounded-sm shadow-sm">
                            {isWine ? (WINE_LABEL[(w.wine_type || "").toLowerCase()] || "Vinho") : product.category}
                        </span>
                    </div>
                    {onFavorite && (
                        <button
                            onClick={(e) => { e.preventDefault(); onFavorite(product); }}
                            className="w-9 h-9 bg-cream/90 backdrop-blur-sm hover:bg-cream rounded-full flex items-center justify-center transition-colors shadow-sm"
                            data-testid={`favorite-${product.id}`}
                        >
                            <Heart className={`h-4 w-4 ${isFavorite ? "fill-wine text-wine" : "text-wine"}`} strokeWidth={1.5} />
                        </button>
                    )}
                </div>
                {out && (
                    <div className="absolute inset-0 bg-wine-dark/45 flex items-center justify-center">
                        <span className="label-overline text-cream text-xs bg-wine-dark/80 px-4 py-2 rounded-sm">Esgotado</span>
                    </div>
                )}
            </div>

            <div className="p-5 flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-1.5 text-wine/60">
                    <MapPin className="h-3 w-3" strokeWidth={2} />
                    <span className="label-overline text-[9px] truncate">
                        {product.region}{product.producer_name ? ` · ${product.producer_name}` : ""}
                    </span>
                </div>

                <h3 className="font-heading text-lg leading-snug text-wine-dark line-clamp-2 min-h-[2.6rem]">
                    {product.name}
                </h3>

                {/* Avaliações */}
                <div className="flex items-center gap-1.5" data-testid={`rating-${product.id}`}>
                    <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                                key={i}
                                className={`h-3.5 w-3.5 ${i <= rounded ? "fill-gold-soft text-gold-soft" : "fill-wine-dark/10 text-wine-dark/15"}`}
                                strokeWidth={1}
                            />
                        ))}
                    </div>
                    {reviews > 0 ? (
                        <span className="text-[11px] text-wine-dark/60">
                            <span className="font-medium text-wine-dark/80">{rating.toFixed(1)}</span> ({reviews})
                        </span>
                    ) : (
                        <span className="text-[11px] text-wine-dark/45">Novo</span>
                    )}
                </div>

                {wineMeta && (
                    <p className="text-xs text-wine-dark/55 leading-relaxed line-clamp-1">{wineMeta}</p>
                )}

                {/* Selos de confiança */}
                <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className="inline-flex items-center gap-1 text-[9px] label-overline text-vineyard bg-vineyard/10 px-2 py-1 rounded-sm">
                        <Truck className="h-3 w-3" strokeWidth={1.8} /> Envio 24-48h
                    </span>
                    <span className="inline-flex items-center gap-1 text-[9px] label-overline text-wine bg-wine/8 px-2 py-1 rounded-sm">
                        <BadgeCheck className="h-3 w-3" strokeWidth={1.8} /> Produtor certificado
                    </span>
                </div>

                <div className="mt-auto pt-4 flex items-end justify-between border-t border-gold-soft/15">
                    <div className="flex flex-col">
                        <span className="font-heading text-2xl text-wine-dark leading-none">
                            €{Number(product.price).toFixed(2)}
                        </span>
                        <span className="text-[10px] text-wine-dark/45 mt-1">
                            {isWine && w.bottle_volume_ml ? `Garrafa ${w.bottle_volume_ml}ml` : "Preço unitário"}
                            {low && <span className="text-gold-deep"> · Últimas {stock}</span>}
                        </span>
                    </div>
                </div>

                <button
                    onClick={handleBuy}
                    disabled={out}
                    className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-md label-overline text-[11px] transition-all duration-300
                        ${out
                            ? "bg-wine-dark/10 text-wine-dark/40 cursor-not-allowed"
                            : "bg-wine text-cream hover:bg-wine-dark shadow-sm hover:shadow-md"}`}
                    data-testid={`buy-${product.id}`}
                >
                    <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.8} />
                    {out ? "Esgotado" : "Comprar"}
                </button>
            </div>
        </Link>
    );
}
